import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, Form, FormInput, Icon } from '@connectcore/connect-components-react'
import { CallbackType } from '@forgerock/javascript-sdk'
import { IUserContextType, UserContext } from '~utils/context/UserContext'
import { ALIGN_BUTTONS_AND_LINKS } from '~utils/featureFlags'
import {
  ABOUT_TO_EXPIRE_PASSWORD_MESSAGE,
  FORGEROCK_FORGOT_PASSWORD_STAGES,
  FORGEROCK_LOGIN_STAGES,
  FR_SKIP_PASSWORD_PARAM_VALUE,
  LOGIN_FAILURE_SCD_ERROR,
  PASSWORD_MISMATCH,
  RESETPASSWORD_REQUIRED_MESSAGE,
  PASSWORD_CONTAINS_SPACES_MESSAGE,
  PASSWORD_RESET_ERRORS,
  PASSWORD_REQUIRED_MESSAGE,
  EXPIRED_PASSWORD_ERROR_MESSAGE,
} from '~utils/constants'

interface IFormData {
  formData: { newPassword: string | undefined; confirmPassword: string | undefined }
  feedback: { newPassword: string | undefined; confirmPassword: string | undefined }
}

const ResetPassword = () => {
  const [formState, setFormState] = useState<IFormData>({
    formData: { newPassword: '', confirmPassword: '' },
    feedback: { newPassword: undefined, confirmPassword: undefined },
  })
  const [showNewPass, setShowNewPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [apiError, setApiError] = useState(false)
  const { step, nextStep, showErr, userInputError } = useContext(UserContext) as IUserContextType
  const [daysForPasswordExpiry, setDaysForPasswordExpiry] = useState(0)
  const stage: string = step?.payload?.stage
  let textCallBackFn

  const resetPassRef = useRef<any>(null)
  if (step?.getCallbacksOfType(CallbackType.TextInputCallback)?.length > 0)
    textCallBackFn = step?.getCallbackOfType(CallbackType.TextInputCallback)

  let feedback = {}
  const validateAndSetFormData = e => {
    setApiError(false)
    if (Object.keys(e).includes('newPassword') && !e?.newPassword?.trim()) {
      feedback = { newPassword: PASSWORD_REQUIRED_MESSAGE }
    } else if (e?.newPassword && e.newPassword !== e.newPassword.trim()) {
      feedback = { newPassword: PASSWORD_CONTAINS_SPACES_MESSAGE }
    } else if (Object.keys(e).includes('newPassword') && e.newPassword) {
      feedback = { newPassword: undefined }
    }

    if (Object.keys(e).includes('confirmPassword') && !e?.confirmPassword?.trim()) {
      feedback = { confirmPassword: RESETPASSWORD_REQUIRED_MESSAGE }
    } else if (e?.confirmPassword && e.confirmPassword !== e.confirmPassword.trim()) {
      feedback = { confirmPassword: PASSWORD_CONTAINS_SPACES_MESSAGE }
    } else if (Object.keys(e).includes('confirmPassword') && e?.confirmPassword) {
      feedback = { confirmPassword: undefined }
    }

    setFormState({
      ...formState,
      feedback: { ...formState.feedback, ...feedback },
      formData: { ...formState.formData, ...e },
    })
  }

  const submitNewPassword = () => {
    resetPassRef.current.focus()
    const newPassword = formState.formData.newPassword
    const confirmPassword = formState.formData.confirmPassword

    if (newPassword === '' || confirmPassword === '') {
      const newPasswordFeedback = newPassword === '' ? PASSWORD_REQUIRED_MESSAGE : undefined
      const confirmPasswordFeedback =
        confirmPassword === '' ? RESETPASSWORD_REQUIRED_MESSAGE : undefined
      const feedback = {
        newPassword: newPasswordFeedback,
        confirmPassword: confirmPasswordFeedback,
      }
      setFormState({ ...formState, feedback: { ...formState.feedback, ...feedback } })
    } else if (!formState.feedback.newPassword && !formState.feedback.confirmPassword) {
      if (newPassword === confirmPassword) {
        textCallBackFn?.setInputValue(newPassword)
        nextStep(step)
      } else {
        setErrorMessage(PASSWORD_MISMATCH)
      }
    }
  }

  const changePasswordLater = () => {
    textCallBackFn?.setInputValue(FR_SKIP_PASSWORD_PARAM_VALUE)
    nextStep(step)
  }

  const reset = () => {
    setErrorMessage('')
    const formStateData = { newPassword: '', confirmPassword: '' }
    const feedbackData = { newPassword: '', confirmPassword: '' }
    setFormState({
      ...formState,
      feedback: { ...formState.feedback, ...formStateData },
      formData: { ...formState.formData, ...feedbackData },
    })
  }

  useEffect(() => {
    const daysToExpiryJSON = JSON.parse(textCallBackFn?.getOutputByName('prompt'))?.[0]

    const {
      passwordInfo: { daysToExpiry: daysToExpire },
    } = daysToExpiryJSON

    if (Number.isInteger(daysToExpire) && daysToExpire >= 0) {
      if (daysToExpire > 0) {
        setDaysForPasswordExpiry(daysToExpire)
        setErrorMessage(ABOUT_TO_EXPIRE_PASSWORD_MESSAGE(daysToExpire))
      } else if (daysToExpire === 0) {
        setDaysForPasswordExpiry(daysToExpire)
        setErrorMessage(EXPIRED_PASSWORD_ERROR_MESSAGE)
      }
    }
  }, [])

  useEffect(() => {
    setApiError(showErr)
  }, [showErr])

  useEffect(() => {
    if (userInputError) {
      const errMsg = PASSWORD_RESET_ERRORS[userInputError]
        ? PASSWORD_RESET_ERRORS[userInputError]
        : userInputError
      setErrorMessage(errMsg)
    }
  }, [userInputError])

  return (
    <div className="row col-12 p-5">
      {stage === FORGEROCK_LOGIN_STAGES.LOGIN_RESET_PASSWORD && (
        <h2 className="fw-bold my-5 align-self-center">Step 4 - Update your password</h2>
      )}

      {stage === FORGEROCK_FORGOT_PASSWORD_STAGES.FORGOT_PASSWORD_RESET && (
        <h2 className="fw-bold my-5 align-self-center">Password change - Step 4 Change password</h2>
      )}
      <div className="col-md-6 col-12">
        <Form
          className="ResetPasswordForm w-75"
          data-testid="ResetPasswordForm"
          onChange={e => validateAndSetFormData(e)}
          key="ResetPasswordForm"
        >
          <FormInput
            className="my-5"
            name="newPassword"
            value={formState.formData.newPassword}
            feedback={<p role="alert">{formState.feedback.newPassword}</p>}
            innerRef={resetPassRef}
            validationState={
              formState.feedback.newPassword !== undefined &&
              formState.feedback.newPassword?.length > 0
                ? 'invalid'
                : undefined
            }
            required={true}
            label="Password"
            iconOverlay
            type={showNewPass ? 'text' : 'password'}
            appendWith={
              <Icon
                className="p-0"
                name={showNewPass ? 'hide' : 'view-watchlist'}
                actionable
                onClick={() => {
                  setShowNewPass(!showNewPass)
                }}
                aria-label={showNewPass ? 'Hide new password' : 'Show new password'}
              />
            }
          />

          <FormInput
            className="my-5"
            name="confirmPassword"
            required={true}
            value={formState.formData.confirmPassword}
            feedback={<p role="alert">{formState.feedback.confirmPassword}</p>}
            validationState={
              formState.feedback.confirmPassword !== undefined &&
              formState.feedback.confirmPassword?.length > 0
                ? 'invalid'
                : undefined
            }
            label="Confirm Password"
            iconOverlay
            type={showConfirmPass ? 'text' : 'password'}
            appendWith={
              <Icon
                className="p-0"
                name={showConfirmPass ? 'hide' : 'view-watchlist'}
                actionable
                onClick={() => {
                  setShowConfirmPass(!showConfirmPass)
                }}
                aria-label={showConfirmPass ? 'Hide confirm password' : 'Show confirm password'}
              />
            }
          />

          {apiError && (
            <p className="invalid-feedback" role="alert">
              {LOGIN_FAILURE_SCD_ERROR}
            </p>
          )}
          {errorMessage !== '' && (
            <p className="invalid-feedback" role="alert">
              {errorMessage}
            </p>
          )}

          <div className="d-flex">
            <Button
              kind="danger"
              className={`align-${ALIGN_BUTTONS_AND_LINKS} mx-2`}
              onClick={reset}
            >
              Reset
            </Button>
            <Button
              kind="primary"
              className={`align-${ALIGN_BUTTONS_AND_LINKS}`}
              onClick={() => submitNewPassword()}
            >
              Submit
            </Button>
            {daysForPasswordExpiry > 0 && (
              <Button
                kind="primary"
                className={`align-${ALIGN_BUTTONS_AND_LINKS} mx-2`}
                onClick={() => changePasswordLater()}
              >
                Change Later
              </Button>
            )}
          </div>
        </Form>
      </div>
      <div className="col-md-6 col-12">
        <h3>Password Policy: </h3>
        <ul>
          <li>Must be minimum 8 and maximum 127 characters in length</li>
          <li>Must contain at least one alpha and one numeric or symbolic character combination</li>
          <li>
            Cannot contain more than 2 repeating characters (eg., &quot;aa&quot; is allowed but not
            &quot;aaa&quot;)
          </li>
          <li>Cannot contain leading or trailing spaces; spaces in between are allowed</li>
          <li>Cannot contain personal information such as name, email, username, etc..</li>
          <li>Cannot re-use previous 12 passwords</li>
          <li>
            Should be comprised of at least 3 out of the following 4 elements:
            <ul className="pl-5">
              <li>Lowercase characters (a through z)</li>
              <li>Uppercase characters (A through Z)</li>
              <li>Base 10 digits (0 through 9)</li>
              <li>Special or non-alphanumeric characters (@,#,+,etc)</li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  )
}

export default ResetPassword
